import { compact } from 'lodash'
import { useGetPondCaretakers } from 'modules/member/api/queries'
import { selectedCycleIdState } from 'modules/pond/stores/atom'
import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useParams } from 'react-router-dom'
import { useRecoilValue } from 'recoil'

import { SpeciesType } from '../constants'
import { useGetPondNames, useGetPonds, useGetCyclesByFarmByPond } from './queries'
import { CycleCaretaker, CycleHistory } from './types'

export const usePondInfoById = (pondId: string) => {
  const { data } = useGetPonds()
  const allPonds = data?.pages?.flatMap((page) => page?.data ?? []) ?? []
  const pond = allPonds.find((item) => pondId === item.id)
  return pond
}

export const usePondCaretakers = () => {
  const { data, ...rest } = useGetPondCaretakers()

  return useMemo(
    () => ({
      ...rest,
      data: (data ?? []).map((caretaker) => ({
        id: caretaker.id,
        name: caretaker.name,
        photo: caretaker?.pictureURL,
      })) as CycleCaretaker[] | [],
    }),

    [data, rest.isFetching]
  )
}

export const useSpeciesName = () => {
  const { pondId } = useParams()
  const { data: pond } = useGetPondNameById(pondId ?? '')

  return pond?.species?.name ?? ''
}

export const useSpeciesId = () => {
  const { pondId } = useParams()
  const { data: pond } = useGetPondNameById(pondId ?? '')

  return pond?.species?.id ?? 0
}

export const useIsShrimpSpecie = () => {
  const { pondId } = useParams()
  const { data: pond } = useGetPondNameById(pondId ?? '')

  return pond?.species?.id === 1
}

export const useIsFishSpecie = () => {
  const { pondId } = useParams()
  const { data: pond } = useGetPondNameById(pondId ?? '')

  return pond?.species?.id === 2
}

export const useGetXTitleLineChart = () => {
  const { t } = useTranslation('pond')
  const speciesName = useSpeciesName()

  return `${t('age')}${speciesName} (${t('day')})`
}

export const useGetSpeciesSizeUnit = (speciesId?: number) => {
  const { t } = useTranslation('pond')

  if (speciesId === SpeciesType.Fish) {
    return t('gram.per.one')
  }

  return t('amount.per.kg.unit')
}

export const useGetPondsRecoilSelector = () => {
  const { data, ...rest } = useGetPonds()

  const flatData = compact(data?.pages.flatMap((page) => page?.data ?? []) ?? [])
  const totalItems = data?.pages?.[0]?.total ?? 0

  return {
    ponds: flatData,
    total: totalItems,
    ...rest,
  }
}

export const useGetPondNameById = (pondId: string) => {
  const { data, ...rest } = useGetPondNames()
  const pond = data?.find((item) => item.id === pondId)

  return {
    data: pond,
    ...rest,
  }
}

export const useCurrentCycleInfo = (): CycleHistory | null => {
  const { farmId, pondId } = useParams()
  const cycleId = useRecoilValue(selectedCycleIdState)
  const { data: cycles } = useGetCyclesByFarmByPond(farmId ?? '', pondId ?? '')

  return useMemo(
    () => cycles?.find((c: CycleHistory) => c.id === cycleId) ?? null,
    [cycles, cycleId]
  )
}
